import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';

import { 
  RiDeleteBin6Line,
  RiSubtractLine,
  RiAddLine,
  RiShoppingCartLine,
  RiArrowRightLine,
  RiSecurePaymentLine,
  RiTruckLine,
  RiRefund2Line,
  RiLoader4Line,
  RiHeartLine,
  RiArrowGoBackLine
} from 'react-icons/ri';
import '../styles/Cart.css';
import { useApiCall } from '../hooks/useApiCall';

export default function Cart() {
  const navigate = useNavigate();
  const { account } = useAccount();
  const { refreshCounts } = useCart();
  const { showSuccess, showError } = useToast();
  const apiCall = useApiCall();

  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState({});
  const [cartSummary, setCartSummary] = useState({
    totalItems: 0,
    totalAmount: 0
  });
  const [pendingDeletions, setPendingDeletions] = useState({});
  const [undoTimeouts, setUndoTimeouts] = useState({});
  const [deletedItems, setDeletedItems] = useState({}); // Store deleted item data for undo

  // Fetch cart on component mount
  useEffect(() => {
    if (account) {
      fetchCart();
    } else {
      setIsLoading(false);
    }
  }, [account]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(undoTimeouts).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, [undoTimeouts]);

  const fetchCart = async () => {
    if (!account) return;
    
    setIsLoading(true);
    try {
      const response = await apiCall('account/cart/get', 'POST', {}, account, null);
      
      if (response.success) {
        setCartItems(response.data.cart || []);
        setCartSummary({
          totalItems: response.data.total_items || 0,
          totalAmount: response.data.total_amount || 0
        });
      } else {
        showError('Failed to load cart');
        console.error('Cart fetch failed:', response.message);
      }
    } catch (error) {
      showError('Failed to load cart');
      console.error('Error fetching cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    if (!account) {
      showError('Please sign in to update your cart');
      return;
    }
    
    setIsUpdating(prev => ({ ...prev, [cartId]: true }));

    try {
      const response = await apiCall('account/cart/update', 'POST', {
        cart_id: cartId,
        qty: newQuantity
      }, account, null);
      
      if (response.success) {
        // Update local state
        const updatedItems = cartItems.map(item => 
          item.cart_id === cartId 
            ? { ...item, qty: newQuantity }
            : item
        );
        setCartItems(updatedItems);
        
        // Recalculate totals locally to avoid flicker
        const newTotalItems = updatedItems.reduce((sum, item) => sum + parseInt(item.qty), 0);
        const newTotalAmount = updatedItems.reduce((sum, item) => {
          const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
          const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
          return sum + (itemPrice * parseInt(item.qty));
        }, 0);
        
        setCartSummary({
          totalItems: newTotalItems,
          totalAmount: newTotalAmount
        });
        refreshCounts();
      } else {
        showError(response.message || 'Failed to update quantity');
      }
    } catch (error) {
      showError('Failed to update quantity');
      console.error('Error updating quantity:', error);
    } finally {
      setIsUpdating(prev => ({ ...prev, [cartId]: false }));
    }
  };

  const removeItem = async (productId, cartId) => {
    try {
      // Clear any existing timeout for this item first
      if (undoTimeouts[cartId]) {
        clearTimeout(undoTimeouts[cartId]);
      }
      
      // Find and save the item data before removing it
      const itemToDelete = cartItems.find(item => item.cart_id === cartId);
      if (!itemToDelete) return;
      
      // Store the deleted item data for potential undo
      setDeletedItems(prev => ({ ...prev, [cartId]: itemToDelete }));
      
      // Delete from database IMMEDIATELY
      await apiCall('account/cart/remove', 'POST', { cart_id: cartId }, account, null);
      
      // Mark item as pending deletion (for UI display) - use cart_id as key
      setPendingDeletions(prev => ({ ...prev, [cartId]: true }));
      
      // Update cart summary immediately by excluding this item
      const updatedItems = cartItems.filter(item => item.cart_id !== cartId);
      const newTotalItems = updatedItems.reduce((sum, item) => {
        // Also exclude other items pending deletion
        if (pendingDeletions[item.cart_id] && item.cart_id !== cartId) return sum;
        return sum + parseInt(item.qty);
      }, 0);
      const newTotalAmount = updatedItems.reduce((sum, item) => {
        // Also exclude other items pending deletion
        if (pendingDeletions[item.cart_id] && item.cart_id !== cartId) return sum;
        const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
        const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
        return sum + (itemPrice * parseInt(item.qty));
      }, 0);
      
      setCartSummary({
        totalItems: newTotalItems,
        totalAmount: newTotalAmount
      });
      
      // Trigger count refresh after deletion
      refreshCounts();
      
      // Set timeout to permanently remove from UI after 5 seconds
      const timeoutId = setTimeout(() => {
        permanentlyRemoveFromUI(cartId);
      }, 5000); // 5 seconds to undo
      
      setUndoTimeouts(prev => ({ ...prev, [cartId]: timeoutId }));
      
      showError('Item removed. Undo within 5 seconds to restore.');
    } catch (error) {
      console.error('Error removing item from cart:', error);
      showError('Failed to remove item from cart');
    }
  };

  const permanentlyRemoveFromUI = (cartId) => {
    // Clear timeout reference if it exists
    if (undoTimeouts[cartId]) {
      clearTimeout(undoTimeouts[cartId]);
    }
    
    // Remove from UI state using cart_id
    setCartItems(prevCart => {
      const updatedItems = prevCart.filter(item => item.cart_id !== cartId);
      
      // Recalculate summary with updated items
      const newTotalItems = updatedItems.reduce((sum, item) => sum + parseInt(item.qty), 0);
      const newTotalAmount = updatedItems.reduce((sum, item) => {
        const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
        const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
        return sum + (itemPrice * parseInt(item.qty));
      }, 0);
      
      setCartSummary({
        totalItems: newTotalItems,
        totalAmount: newTotalAmount
      });
      
      return updatedItems;
    });
    
    // Clean up state using cart_id
    setPendingDeletions(prev => {
      const newState = { ...prev };
      delete newState[cartId];
      return newState;
    });
    
    setUndoTimeouts(prev => {
      const newState = { ...prev };
      delete newState[cartId];
      return newState;
    });
    
    setDeletedItems(prev => {
      const newState = { ...prev };
      delete newState[cartId];
      return newState;
    });
  };

  const undoRemoval = async (cartId) => {
    // Clear the timeout first to prevent UI removal
    if (undoTimeouts[cartId]) {
      clearTimeout(undoTimeouts[cartId]);
    }
    
    // Get the deleted item data
    const deletedItem = deletedItems[cartId];
    if (!deletedItem) {
      showError('Unable to restore item');
      return;
    }
    
    try {
      // Re-add item to database with all original data
      const response = await apiCall('account/cart/add', 'POST', {
        product_id: deletedItem.id,
        qty: deletedItem.qty,
        options: deletedItem.options || null
      }, account, null);
      
      if (response.success) {
        // Remove from pending deletions and deleted items
        setPendingDeletions(prev => {
          const newState = { ...prev };
          delete newState[cartId];
          return newState;
        });
        
        setDeletedItems(prev => {
          const newState = { ...prev };
          delete newState[cartId];
          return newState;
        });
        
        setUndoTimeouts(prev => {
          const newState = { ...prev };
          delete newState[cartId];
          return newState;
        });
        
        // Recalculate cart summary to include this item again
        const newTotalItems = cartItems.reduce((sum, item) => {
          // Exclude items that are still pending deletion (but not this one)
          if (pendingDeletions[item.cart_id] && item.cart_id !== cartId) return sum;
          return sum + parseInt(item.qty);
        }, 0);
        const newTotalAmount = cartItems.reduce((sum, item) => {
          // Exclude items that are still pending deletion (but not this one)
          if (pendingDeletions[item.cart_id] && item.cart_id !== cartId) return sum;
          const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
          const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
          return sum + (itemPrice * parseInt(item.qty));
        }, 0);
        
        setCartSummary({
          totalItems: newTotalItems,
          totalAmount: newTotalAmount
        });
        
        // Refresh cart to get the newly added item with its new cart_id
        await fetchCart();
        refreshCounts();
        
        showSuccess('Item restored successfully');
      } else {
        showError(response.message || 'Failed to restore item');
      }
    } catch (error) {
      console.error('Error restoring item:', error);
      showError('Failed to restore item');
    }
  };

  const saveForLater = async (cartId, productId) => {
    if (!account) {
      showError('Please sign in to save items for later');
      return;
    }

    setIsUpdating(prev => ({ ...prev, [cartId]: true }));

    try {
      // Find the cart item to get its options
      const cartItem = cartItems.find(item => item.cart_id === cartId);
      
      // Prepare wishlist data with options if they exist
      const wishlistData = {
        product_id: productId
      };
      
      // Include options (modifiers) if they exist
      if (cartItem && cartItem.options) {
        wishlistData.options = typeof cartItem.options === 'string' ? cartItem.options : JSON.stringify(cartItem.options);
      }
      
      // First add to wishlist
      const addResponse = await apiCall('account/wishlist/add', 'POST', wishlistData, account, null);

      if (addResponse.success) {
        // Then remove from cart
        const removeResponse = await apiCall('account/cart/remove', 'POST', {
          cart_id: cartId
        }, account, null);

        if (removeResponse.success) {
          const updatedItems = cartItems.filter(item => item.cart_id !== cartId);
          setCartItems(updatedItems);
          showSuccess('Item saved to wishlist. Click to view.', 4000, () => navigate('/wishlist'));
          
          // Recalculate totals locally to avoid flicker
          const newTotalItems = updatedItems.reduce((sum, item) => sum + parseInt(item.qty), 0);
          const newTotalAmount = updatedItems.reduce((sum, item) => {
            const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
            const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
            return sum + (itemPrice * parseInt(item.qty));
          }, 0);
          
          setCartSummary({
            totalItems: newTotalItems,
            totalAmount: newTotalAmount
          });
          
          refreshCounts();
        } else {
          showError(removeResponse.message || 'Failed to remove item from cart');
        }
      } else {
        if (addResponse.message && addResponse.message.includes('already in your wishlist')) {
          // If already in wishlist, just remove from cart
          const removeResponse = await apiCall('account/cart/remove', 'POST', {
            cart_id: cartId
          }, account, null);

          if (removeResponse.success) {
            const updatedItems = cartItems.filter(item => item.cart_id !== cartId);
            setCartItems(updatedItems);
            showSuccess('Item removed from cart (already in wishlist). Click to view.', 4000, () => navigate('/wishlist'));
            
            // Recalculate totals locally to avoid flicker
            const newTotalItems = updatedItems.reduce((sum, item) => sum + parseInt(item.qty), 0);
            const newTotalAmount = updatedItems.reduce((sum, item) => {
              const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
              const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
              return sum + (itemPrice * parseInt(item.qty));
            }, 0);
            
            setCartSummary({
              totalItems: newTotalItems,
              totalAmount: newTotalAmount
            });
          }
        } else {
          showError(addResponse.message || 'Failed to save item to wishlist');
        }
      }
    } catch (error) {
      showError('Failed to save item for later');
      console.error('Error saving for later:', error);
    } finally {
      setIsUpdating(prev => ({ ...prev, [cartId]: false }));
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => {
      // Skip items that are pending deletion
      if (pendingDeletions[item.cart_id]) {
        return sum;
      }
      
      const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
      const itemPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
      const qty = parseInt(item.qty);
      let itemTotal = itemPrice * qty;
      
      // Add modifier amounts
      if (item.options) {
        try {
          const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
          if (options && options.modifiers && Array.isArray(options.modifiers)) {
            options.modifiers.forEach(modifier => {
              if (modifier.option && modifier.option.amount) {
                itemTotal += parseFloat(modifier.option.amount) * qty;
              }
            });
          }
        } catch (e) {
          console.error('Error parsing options for price calculation:', e);
        }
      }
      
      return sum + itemTotal;
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = subtotal > 200 ? 0 : 0;
    return subtotal + shipping;
  };

  const getShippingCost = () => {
    return calculateSubtotal() > 200 ? 0 : 0;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-header">
            <h1 className="cart-title">Shopping Cart</h1>
          </div>
          
          <div className="cart-loading">
            <RiLoader4Line className="loading-spinner" />
            <p>Loading your cart...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!account) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-header">
            <h1 className="cart-title">Shopping Cart</h1>
          </div>
          
          <div className="cart-empty">
            <RiShoppingCartLine className="cart-empty-icon" />
            <h2>Sign in to view your cart</h2>
            <p>Save items to your cart and access them anywhere</p>
            <button 
              className="btn-browse-products"
              onClick={() => navigate('/sign-in')}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart
  if (cartItems.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-container">
          <div className="cart-header">
            <h1 className="cart-title">Shopping cart</h1>
          </div>
          
          <div className="cart-empty">
            <RiShoppingCartLine className="cart-empty-icon" />
            <h2>Your cart is empty</h2>
            <p>Add some items to get started</p>
            <button 
              className="btn-continue-shopping"
              onClick={() => navigate('/products')}
            >
              Continue shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        
        <div className="cart-header">
          <h1 className="cart-title">Shopping cart</h1>
          <span className="cart-items-count">{cartItems.length} item{ cartItems.length !== 1 ? 's' : '' }</span>
        </div>

        <div className="cart-content">
          
          {/* Cart Items */}
          <div className="cart-items">
            {cartItems.map(item => {
              const hasDiscount = item.price_sale && parseFloat(item.price_sale) > 0;
              const currentPrice = hasDiscount ? parseFloat(item.price_sale) : parseFloat(item.price);
              const originalPrice = hasDiscount ? parseFloat(item.price) : null;
              const qty = parseInt(item.qty);
              
              // Calculate item total including modifiers
              let itemTotal = currentPrice * qty;
              
              // Add modifier amounts
              if (item.options) {
                try {
                  const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                  if (options && options.modifiers && Array.isArray(options.modifiers)) {
                    options.modifiers.forEach(modifier => {
                      if (modifier.option && modifier.option.amount) {
                        itemTotal += parseFloat(modifier.option.amount) * qty;
                      }
                    });
                  }
                } catch (e) {
                  console.error('Error parsing options for price calculation:', e);
                }
              }
              
              const isPendingDeletion = pendingDeletions[item.cart_id];
              
              // Create slug for product URL
              const createSlug = (name) => {
                return name
                  .toLowerCase()
                  .replace(/[^a-z0-9\s&]/g, '')
                  .replace(/\s*&\s*/g, '-and-')
                  .replace(/\s+/g, '-')
                  .replace(/-+/g, '-')
                  .replace(/^-|-$/g, '');
              };
              const productSlug = createSlug(item.name);
              
              return (
                <div 
                  key={item.cart_id} 
                  className={`cart-item ${isPendingDeletion ? 'pending-deletion' : ''}`}
                  style={{
                    opacity: isPendingDeletion ? 0.5 : 1
                  }}
                >
                  <div 
                    className="cart-item-image"
                    onClick={() => navigate(`/products/product/${item.id}/${productSlug}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img 
                      src={item.primary_image} 
                      alt={item.name} 
                    />
                  </div>
                  
                  <div className="cart-item-details">
                    <div className="cart-item-info">
                      <h3 
                        className="cart-item-name"
                        onClick={() => navigate(`/products/product/${item.id}/${productSlug}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.name}
                      </h3>
                      <div className="cart-item-category">
                        Category: {item.category_name || 'General'}
                      </div>
                      {item.descr && (
                        <div className="cart-item-description">
                          {item.descr}
                        </div>
                      )}
                      {!item.in_stock && (
                        <div className="cart-item-stock-warning">
                          Out of stock - Remove item to continue
                        </div>
                      )}
                      {item.comments && (
                        <div className="cart-item-comments">
                          Note: {item.comments}
                        </div>
                      )}
                      {item.options && (() => {
                        try {
                          const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                          if (options && options.modifiers && options.modifiers.length > 0) {
                            return (
                              <div className="cart-item-modifiers">
                                {options.modifiers.map((modifier, index) => (
                                  <div key={index} className="modifier-item">
                                    <strong>{modifier.name}:</strong> {modifier.option?.item || 'N/A'}
                                    {modifier.option?.amount > 0 && <span className="modifier-price"> (+${modifier.option.amount})</span>}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                        } catch (e) {
                          console.error('Error parsing options:', e);
                        }
                        return null;
                      })()}
                    </div>
                  
                    <div className="cart-item-actions">
                      <div className="cart-item-quantity">
                        <button 
                          onClick={() => updateQuantity(item.cart_id, parseInt(item.qty) - 1)}
                          disabled={parseInt(item.qty) <= 1 || isUpdating[item.cart_id]}
                          className="quantity-btn"
                        >
                          {isUpdating[item.cart_id] ? <RiLoader4Line className="spinning" /> : <RiSubtractLine />}
                        </button>
                        <span className="quantity-display">{item.qty}</span>
                        <button 
                          onClick={() => updateQuantity(item.cart_id, parseInt(item.qty) + 1)}
                          disabled={isUpdating[item.cart_id] || !item.in_stock}
                          className="quantity-btn"
                        >
                          {isUpdating[item.cart_id] ? <RiLoader4Line className="spinning" /> : <RiAddLine />}
                        </button>
                      </div>
                      
                      <div className="cart-item-buttons">
                        <button 
                          onClick={() => saveForLater(item.cart_id, item.id)}
                          disabled={isUpdating[item.cart_id]}
                          className={`btn-save-later ${isUpdating[item.cart_id] ? 'loading' : ''}`}
                          title="Save for Later"
                        >
                          {isUpdating[item.cart_id] ? <RiLoader4Line className="spinning" /> : <RiHeartLine />}
                        </button>
                        
                        {isPendingDeletion ? (
                          <button 
                            onClick={() => undoRemoval(item.cart_id)}
                            className="btn-undo"
                            title="Undo Removal"
                          >
                            <RiArrowGoBackLine />
                          </button>
                        ) : (
                          <button 
                            onClick={() => removeItem(item.id, item.cart_id)}
                            disabled={isUpdating[item.cart_id]}
                            className={`btn-remove ${isUpdating[item.cart_id] ? 'loading' : ''}`}
                            title="Remove Item"
                          >
                            {isUpdating[item.cart_id] ? <RiLoader4Line className="spinning" /> : <RiDeleteBin6Line />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="cart-item-price">
                    <div className="item-price-current">
                      {item.price_curr} {itemTotal.toFixed(2)}
                    </div>
                    {originalPrice && (
                      <div className="item-price-original">
                        {item.price_curr} {(originalPrice * parseInt(item.qty)).toFixed(2)}
                      </div>
                    )}
                    <div className="item-price-unit">
                      {item.price_curr} {currentPrice.toFixed(2)} each
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cart Summary */}
          <div className="cart-summary">
            <div className="summary-section">
              <h3 className="summary-title">Order summary</h3>
              
              <div className="summary-line">
                <span>Subtotal ({cartItems.length} items)</span>
                <span>{cartItems[0].price_curr} {calculateSubtotal().toFixed(2)}</span>
              </div>
              
              <div className="summary-line">
                <span>Shipping</span>
                <span>
                  {getShippingCost() === 0 ? 'Free' : `$${getShippingCost().toFixed(2)}`}
                </span>
              </div>
              
              <div className="summary-line total">
                <span>Total</span>
                <span>{cartItems[0].price_curr} {calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Features */}
            <div className="cart-features">
              <div className="feature-item">
                <RiSecurePaymentLine className="feature-icon" />
                <span>Secure Payment</span>
              </div>
              <div className="feature-item">
                <RiTruckLine className="feature-icon" />
                <span>Free Shipping over $ 200</span>
              </div>
              <div className="feature-item">
                <RiRefund2Line className="feature-icon" />
                <span>30-day Returns</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="cart-actions">
              <button 
                className="btn-continue-shopping-secondary"
                onClick={() => navigate('/products')}
              >
                Continue shopping
              </button>
              <button 
                className="btn-checkout"
                disabled={cartItems.some(item => !item.in_stock)}
                onClick={() => navigate('/checkout')}
              >
                Proceed to checkout
                <RiArrowRightLine />
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}